import { formatDate } from "../../../App/Helpers/Helpers"
import { useSelector } from "react-redux"
import { RootState } from "../../../App/store"
import { ConversationMessage } from "../../../Types/Conversation"
import DOMPurify from "dompurify"
import { memo, useMemo } from "react"
import { selectTranslations } from "../../../App/i18n/i18nSlice"
import { EditProfileSmallIcon } from "../../Shared/Components/SvgIcons"

const ALLOWED_TAGS = ["b", "br", "em", "i", "strong", "u", "s", "p", "span", "a", "div", "img", "header", "footer", "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "pre", "code"]
const ALLOWED_ATTR = ["class", "href", "target", "data-type", "className", "id", "rel", "src", "style", "width", "type", "data-reactroot", "contenteditable"]

const decodeHtmlEntities = (content: string) => {
    const textarea = document.createElement("textarea")
    textarea.innerHTML = content
    return textarea.value
}

const decodeHtmlEntitiesSafely = (content: string) => {
    let decoded = content

    // Some payloads can be double-encoded (e.g. &amp;lt;p&amp;gt;...)
    // so decode a few rounds until the value stabilizes.
    for (let i = 0; i < 3; i++) {
        const nextDecoded = decodeHtmlEntities(decoded)
        if (nextDecoded === decoded) {
            break
        }
        decoded = nextDecoded
    }

    return decoded
}

const hasHtmlTags = (content: string) => /<\/?[a-z][\s\S]*>/i.test(content)

const markdownToHtml = (content: string) => {
    const escaped = content
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")

    const withLinks = escaped.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer nofollow">$1</a>')
    const withBold = withLinks.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    const withItalic = withBold.replace(/\*([^*]+)\*/g, "<em>$1</em>")
    const withLineBreaks = withItalic.replace(/\n/g, "<br>")
    return `<p>${withLineBreaks}</p>`
}

const normalizeLineBreaks = (content: string) => {
    return content
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .replace(/\\r\\n/g, "\n")
        .replace(/\\n/g, "\n")
}

const renderMessageContent = (content: string) => {
    const safeContent = content || ""
    const decoded = decodeHtmlEntitiesSafely(safeContent)
    const source = hasHtmlTags(safeContent) ? safeContent : decoded
    const normalizedSource = hasHtmlTags(source) ? source : normalizeLineBreaks(source)
    const htmlCandidate = hasHtmlTags(normalizedSource) ? normalizedSource : markdownToHtml(normalizedSource)

    return DOMPurify.sanitize(htmlCandidate, {
        ALLOWED_TAGS: ALLOWED_TAGS,
        ALLOWED_ATTR: ALLOWED_ATTR,
    })
}

function ChatMessages(props: { msgarray: Array<ConversationMessage>, onStartEdit: (message: ConversationMessage) => void }) {
    const currentUserId = useSelector((state: RootState) => state.userDataSlice.id)

    return (
        <div className="chat-content">
            {props.msgarray?.map((msg,idx) => {
                let showDate = false
                if(idx === 0){
                    showDate = true
                }

                if(idx > 0 ){
                    let previous_msg_created_at = new Date(props.msgarray[idx-1].created_at)
                    let current_msg_created_at = new Date(msg.created_at)

                    if(previous_msg_created_at.getDate() < current_msg_created_at.getDate()){
                        showDate = true
                    }
                }
                return <MemoChatMessage key={msg.id} message={msg} showDate={showDate} currentUserId={currentUserId} onStartEdit={props.onStartEdit} />
            },)}
        </div>
    )
}

export default memo(ChatMessages)

type IProps = {
    message: ConversationMessage,
    showDate: boolean,
    currentUserId: number
    onStartEdit: (message: ConversationMessage) => void
}

function ChatMessage({ message,showDate, currentUserId, onStartEdit }: IProps) {
    const translation = useSelector(selectTranslations)

    const isCurrentUserMessage = message.author.id === currentUserId
    const isEdited = Boolean(message.is_edited)

    const message_content = useMemo(() => renderMessageContent(message.content), [message.content])
    const parsedDate = new Date(message.created_at)
    return (
        <div className={`chat-message`} id={`${message.id}`}>
            {showDate && (
                <div className="chat-message__date">
                    <span> {parsedDate.getDate()} {parsedDate.toLocaleString('de-de',{month: "short"})}</span>
                </div>
            )}

            {showDate && (
                <div className="chat-message__break"></div>
            )}

            {!isCurrentUserMessage ? (
                <div className="chat-message__left-panel">
                    <div className={`chat-message__post-content chat-message__post-content__left`}>
                        <div
                            title={`${message.author.first_name} ${message.author.last_name}, ${formatDate(message.created_at, "HH:mm dd.MM.yy")}`}
                            className={`chat-message__post-text-container`}
                        >
                            <div className={`chat-message__post-text`}>
                                <div className="chat-message-text chat-message-text__left">
                                    <div dangerouslySetInnerHTML={{ __html: message_content }}></div>
                                    {isEdited && (
                                        <div className="chat-card-item__text" style={{ fontSize: "10px", marginTop: "0.2rem", opacity: 0.8, fontStyle: "italic" }}>
                                            {translation.edited}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="chat-message__right-panel">
                    <div className={`chat-message__post-content chat-message__post-content__right`}>
                        <div
                            title={`${message.author.first_name} ${message.author.last_name}, ${formatDate(message.created_at, "HH:mm dd.MM.yy")}`}
                            className={`chat-message__post-text-container `}
                        >
                            <div className={`chat-message__post-text`}>
                                <>
                                    <div className="chat-message-text chat-message-text__right">
                                        <div dangerouslySetInnerHTML={{ __html: message_content }}></div>
                                        {isEdited && (
                                            <div className="chat-card-item__text" style={{ fontSize: "10px", marginTop: "0.2rem", textAlign: "right", opacity: 0.8, fontStyle: "italic" }}>
                                                {translation.edited}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.4rem", marginTop: "0.25rem" }}>
                                        <span title={translation.edit} style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", lineHeight: 0, opacity: 0.9 }} onClick={() => onStartEdit(message)}>
                                            <span style={{ display: "inline-block", transform: "scale(0.5)", transformOrigin: "center", lineHeight: 0 }}>
                                                <EditProfileSmallIcon />
                                            </span>
                                        </span>
                                    </div>
                                </>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

const MemoChatMessage = memo(ChatMessage, (prevProps, nextProps) => {
    return (
        prevProps.showDate === nextProps.showDate
        && prevProps.currentUserId === nextProps.currentUserId
        && prevProps.message.id === nextProps.message.id
        && prevProps.message.content === nextProps.message.content
        && prevProps.message.created_at === nextProps.message.created_at
        && prevProps.message.is_edited === nextProps.message.is_edited
        && prevProps.message.author.id === nextProps.message.author.id
    )
})
